import { type Frame } from '@/karehero-app/tell-us-once/intake/Flow';
import { type Scene } from '@/designs-dev/scenes';

// The rows of the flows page, in the order it draws them, then everything the
// page holds as a state rather than a step. The select groups by these and the
// chevrons walk them in this order, so stepping forward from "You're in" is the
// Intake row then the Review row, the way the flows page reads.
export const INTAKE_SECTIONS = [
  { id: 'intake', label: 'Intake' },
  { id: 'review', label: 'Review' },
  { id: 'calculator', label: 'Benefits calculator' },
  { id: 'v11', label: 'Onboarding 3.1, warmed up' },
  { id: 'v20', label: 'Onboarding 4.0, what they need first' },
  { id: 'v21', label: 'Onboarding 4.1' },
  { id: 'not-right', label: 'When it is not quite right' },
  { id: 'cannot-read', label: 'When we cannot read it' },
  { id: 'from-app', label: 'Getting there from the app' },
  { id: 'states', label: 'Other states' },
  { id: 'errors', label: 'Errors' },
  { id: 'self', label: 'If they need care themselves' },
  { id: 'previous', label: 'Previous version: the follow-up question' },
  { id: 'off-page', label: 'Not on the flows page' },
  // Last: the B side of the test is only walked when it is picked.
  { id: 'v20-b', label: 'A/B test, option B' },
  { id: 'v21-b', label: 'A/B test, option B' },
];

// The journeys the scenes belong to. Onboarding is every section but the
// calculator's; the calculator is the one opened from Home's card. Ids keep the
// design's old numbers (2.0 is 4.0, 2.1 is 4.1); the labels say the real ones.
export type IntakeJourney =
  | 'onboarding'
  | 'onboarding-1-1'
  | 'onboarding-2-0'
  | 'onboarding-2-1'
  | 'calculator';

// The versions the prototype's pill offers. The calculator is not one: it is a
// separate piece of work with its own pages, so it is not on the pill even
// though its scenes still exist for those pages.
export const INTAKE_JOURNEYS: { id: IntakeJourney; label: string }[] = [
  { id: 'onboarding', label: 'Version 3.0' },
  { id: 'onboarding-1-1', label: 'Version 3.1' },
  { id: 'onboarding-2-0', label: 'Version 4.0' },
  { id: 'onboarding-2-1', label: 'Version 4.1' },
];

export const journeyOfSection = (section?: string): IntakeJourney =>
  section === 'calculator'
    ? 'calculator'
    : section === 'v11'
      ? 'onboarding-1-1'
      : section === 'v20' || section === 'v20-b'
        ? 'onboarding-2-0'
        : section === 'v21' || section === 'v21-b'
          ? 'onboarding-2-1'
          : 'onboarding';

// Every screen of the chat flow, so the prototype can be opened at any of them
// rather than clicked to from the start. Within a section the order is the
// flows page's step order, so keep the two in step when either moves.
export const intakeScenes: Scene<Frame>[] = [
  // Intake
  {
    id: 'youre-in',
    label: "You're in",
    kind: 'happy',
    initial: 'youre-in',
    section: 'intake',
  },
  {
    id: 'ai-consent',
    label: 'Before we start',
    kind: 'happy',
    initial: 'ai-consent',
    section: 'intake',
  },
  {
    id: 'chat-v2',
    label: 'Are you supporting someone?',
    kind: 'happy',
    initial: 'chat-v2-fork',
    section: 'intake',
  },
  {
    id: 'chat-v2-carer-name',
    label: "What's your name?",
    kind: 'happy',
    initial: 'chat-v2-carer-name',
    section: 'intake',
  },
  {
    id: 'chat-v2-preferred-name',
    label: "Anything you'd prefer to be called",
    kind: 'happy',
    initial: 'chat-v2-preferred-name',
    section: 'intake',
  },
  {
    id: 'chat-v2-mobile',
    label: 'Your mobile number',
    kind: 'happy',
    initial: 'chat-v2-mobile',
    section: 'intake',
  },
  {
    id: 'chat-v2-who',
    label: 'Their full name',
    kind: 'happy',
    initial: 'chat-v2-who',
    section: 'intake',
  },
  {
    id: 'chat-v2-situation',
    label: 'Tell us about it',
    kind: 'happy',
    initial: 'chat-v2-situation',
    section: 'intake',
  },
  {
    id: 'chat-v2-situation-photo',
    label: 'A photo ready to send',
    kind: 'happy',
    initial: 'chat-v2-situation-photo',
    section: 'intake',
  },
  {
    id: 'chat-v2-recording',
    label: 'Recording',
    kind: 'happy',
    initial: 'chat-v2-recording',
    section: 'intake',
  },
  {
    id: 'chat-v2-voice',
    label: 'Heard back before it sends',
    kind: 'happy',
    initial: 'chat-v2-voice',
    section: 'intake',
  },
  {
    id: 'chat-v2-anything-more',
    label: 'Is that it?',
    kind: 'happy',
    initial: 'chat-v2-anything-more',
    section: 'intake',
  },
  {
    id: 'chat-v2-anything-more-added',
    label: 'Is that it? After sending more',
    kind: 'happy',
    initial: 'chat-v2-anything-more-added',
    section: 'intake',
  },
  {
    id: 'processing',
    label: 'Processing everything you told us',
    kind: 'happy',
    initial: 'processing',
    section: 'intake',
  },

  // Review
  {
    id: 'summary',
    label: 'Does this look right?',
    kind: 'happy',
    initial: 'summary',
    section: 'review',
  },
  {
    id: 'summary-editing',
    label: 'Editing the summary',
    kind: 'happy',
    initial: 'summary-editing',
    section: 'review',
  },
  {
    id: 'review-facts',
    label: 'About Margaret',
    kind: 'happy',
    initial: 'review-facts',
    section: 'review',
  },
  {
    id: 'review-facts-kept',
    label: 'Or framed as what we kept',
    kind: 'edge',
    initial: 'review-facts-kept',
    section: 'review',
  },
  {
    id: 'home',
    label: 'Into the app',
    kind: 'happy',
    initial: 'home',
    section: 'review',
  },

  // Benefits calculator, from Home's card. The flows page for it is
  // /tell-us-once/flows/benefits-calculator.
  {
    id: 'calc-start',
    label: 'Three we already have',
    kind: 'happy',
    initial: 'calc-start',
    section: 'calculator',
  },
  {
    id: 'calc-paperwork',
    label: 'Her health, and any paperwork',
    kind: 'happy',
    initial: 'calc-paperwork',
    section: 'calculator',
  },
  {
    id: 'calc-read',
    label: 'What it answered',
    kind: 'happy',
    initial: 'calc-read',
    section: 'calculator',
  },
  {
    id: 'calc-questions',
    label: 'The questions',
    kind: 'happy',
    initial: 'calc-questions',
    section: 'calculator',
  },
  {
    id: 'calc-result',
    label: 'What she could claim',
    kind: 'happy',
    initial: 'calc-result',
    section: 'calculator',
  },

  // When it is not quite right. The row opens on the summary and ends on the
  // reading, both already above, so only the screens between them are listed.
  {
    id: 'chat-v2-again',
    label: 'Back in the chat to put it right',
    kind: 'happy',
    initial: 'chat-v2-again',
    section: 'not-right',
  },
  {
    id: 'chat-v2-again-answered',
    label: 'Saying what changed',
    kind: 'happy',
    initial: 'chat-v2-again-answered',
    section: 'not-right',
  },

  // When we cannot read it
  {
    id: 'summary-problem',
    label: 'What we did get',
    kind: 'error',
    initial: 'summary-problem',
    section: 'cannot-read',
  },

  // Getting there from the app
  {
    id: 'intake-actions',
    label: 'Waiting on the actions list',
    kind: 'happy',
    initial: 'intake-actions',
    section: 'from-app',
  },
  {
    id: 'care-profile-unreviewed',
    label: 'The same facts on the profile, unreviewed',
    kind: 'happy',
    initial: 'care-profile-unreviewed',
    section: 'from-app',
  },

  // Other states: the alternatives the flows page stacks on a step, and the
  // states it lists under a section.
  {
    id: 'youre-in-consent',
    label: "You're in, consent on Continue",
    kind: 'edge',
    initial: 'youre-in-consent',
    section: 'states',
  },
  {
    id: 'ai-consent-declined',
    label: 'If they say no',
    kind: 'edge',
    initial: 'ai-consent-declined',
    section: 'states',
  },
  {
    id: 'chat-v2-reading',
    label: 'The beat before we reply',
    kind: 'edge',
    initial: 'chat-v2-reading',
    section: 'states',
  },
  {
    id: 'chat-v2-who-photo',
    label: 'A photo of their ID instead',
    kind: 'edge',
    initial: 'chat-v2-who-photo',
    section: 'states',
  },
  {
    id: 'chat-v2-who-photo-uploading',
    label: 'That photo going',
    kind: 'edge',
    initial: 'chat-v2-who-photo-uploading',
    section: 'states',
  },
  {
    id: 'chat-v2-more-doc-uploading',
    label: 'That document going',
    kind: 'edge',
    initial: 'chat-v2-more-doc-uploading',
    section: 'states',
  },
  {
    id: 'chat-v2-more-doc',
    label: 'A document handed over',
    kind: 'edge',
    initial: 'chat-v2-more-doc',
    section: 'states',
  },
  {
    id: 'chat-v2-attach',
    label: 'The plus, open',
    kind: 'edge',
    initial: 'chat-v2-attach',
    section: 'states',
  },
  {
    id: 'processing-in-app',
    label: 'Processing, inside the app',
    kind: 'edge',
    initial: 'processing-in-app',
    section: 'states',
  },
  {
    id: 'care-profile-arriving',
    label: 'Arriving on the profile, sheet up',
    kind: 'edge',
    initial: 'care-profile-arriving',
    section: 'states',
  },
  {
    id: 'review-facts-read',
    label: 'Changing something we read',
    kind: 'edge',
    initial: 'review-facts-read',
    section: 'states',
  },
  {
    id: 'review-facts-gap',
    label: 'Filling something nobody answered',
    kind: 'edge',
    initial: 'review-facts-gap',
    section: 'states',
  },
  {
    id: 'review-facts-conflict',
    label: 'Two sources disagree',
    kind: 'edge',
    initial: 'review-facts-conflict',
    section: 'states',
  },
  {
    id: 'review-facts-stale',
    label: 'A fact that has gone stale',
    kind: 'edge',
    initial: 'review-facts-stale',
    section: 'states',
  },

  // Errors
  {
    id: 'fail-upload-photo',
    label: 'That photo would not upload',
    kind: 'error',
    initial: 'fail-upload-photo',
    section: 'errors',
  },
  {
    id: 'fail-upload-doc',
    label: 'That document would not upload',
    kind: 'error',
    initial: 'fail-upload-doc',
    section: 'errors',
  },
  {
    id: 'fail-voice',
    label: 'A recording would not transcribe',
    kind: 'error',
    initial: 'fail-voice',
    section: 'errors',
  },
  {
    id: 'fail-nothing',
    label: 'Nothing useful came out of it',
    kind: 'error',
    initial: 'fail-nothing',
    section: 'errors',
  },
  {
    id: 'fail-mobile',
    label: 'A mobile number that is too short',
    kind: 'error',
    initial: 'fail-mobile',
    section: 'errors',
  },
  {
    id: 'fail-one-name',
    label: 'Only a first name',
    kind: 'error',
    initial: 'fail-one-name',
    section: 'errors',
  },
  {
    id: 'fail-send',
    label: 'A message would not send',
    kind: 'error',
    initial: 'fail-send',
    section: 'errors',
  },
  {
    id: 'fail-send-voice',
    label: 'A voice note would not send',
    kind: 'error',
    initial: 'fail-send-voice',
    section: 'errors',
  },

  // The other fork, in its own section, the way the flows page draws it as an
  // alternative under the row.
  {
    id: 'chat-v2-chosen-self',
    label: 'Or they need care themselves',
    kind: 'edge',
    initial: 'chat-v2-chosen-self',
    section: 'self',
  },
  {
    id: 'chat-v2-who-self',
    label: 'Or their own name',
    kind: 'edge',
    initial: 'chat-v2-who-self',
    section: 'self',
  },
  {
    id: 'self-branch',
    label: 'Where the self branch stops',
    kind: 'edge',
    initial: 'self-branch',
    section: 'self',
  },

  // The collapsed row on the flows page.
  {
    id: 'chat-v2-more',
    label: 'Send as many as you want',
    kind: 'edge',
    initial: 'chat-v2-more',
    section: 'previous',
  },
  {
    id: 'chat-v2-stuck',
    label: 'Five notes later',
    kind: 'edge',
    initial: 'chat-v2-stuck',
    section: 'previous',
  },

  // Screens from before the review was cut to one run. Still reachable, last in
  // the list, so walking the flow never lands on them.
  {
    id: 'choose-next',
    label: 'What would you like to do next?',
    kind: 'edge',
    initial: 'choose-next',
    section: 'off-page',
  },
  {
    id: 'filled',
    label: 'What that filled in',
    kind: 'edge',
    initial: 'filled',
    section: 'off-page',
  },
  {
    id: 'calculator',
    label: 'Benefits calculator',
    kind: 'edge',
    initial: 'calculator',
    section: 'off-page',
  },
  {
    id: 'eligible',
    label: 'What the calculator found',
    kind: 'edge',
    initial: 'eligible',
    section: 'off-page',
  },
  {
    id: 'review',
    label: 'Review Attendance Allowance',
    kind: 'edge',
    initial: 'review',
    section: 'off-page',
  },
  {
    id: 'book-call',
    label: 'Talk it through',
    kind: 'edge',
    initial: 'book-call',
    section: 'off-page',
  },
  {
    id: 'care-profile',
    label: 'Details, claims, docs, calls',
    kind: 'edge',
    initial: 'care-profile',
    section: 'off-page',
  },
];

/**
 * Which screen in the list each of the chat's questions is. The chat asks all of
 * them inside one frame, so a stage watching frames alone would sit on the name
 * of the screen the chat opened at while the reader answered their way past it.
 *
 * Keyed by question id, so reordering the conversation cannot silently point a
 * screen at the wrong question.
 */
/**
 * 2.0's questions by the frame that shows each, so clicking through the chat
 * moves the prototype's screen name along the Happy path. Where to start is
 * its Happy path frame, with Book a call.
 */
export const INTAKE_CHAT_FRAMES_V20: Record<string, Frame> = {
  consent: 'chat-v2',
  help: 'help-ask',
  summary: 'help-summary',
  'need-help': 'help-need-help',
  'summary-need': 'help-need-help-summary',
  'not-quite': 'help-not-quite',
  'summary-fixed': 'help-summary-fixed',
  location: 'help-questions',
  age: 'help-age',
  'under-18': 'help-under-18',
  phone: 'help-phone',
  start: 'help-start-talk',
};

export const INTAKE_CHAT_SCENES: Record<string, string> = {
  'carer-name': 'chat-v2-carer-name',
  'carer-preferred-name': 'chat-v2-preferred-name',
  'carer-mobile': 'chat-v2-mobile',
  fork: 'chat-v2',
  who: 'chat-v2-who',
  situation: 'chat-v2-situation',
  'anything-more': 'chat-v2-anything-more',
  'anything-else': 'chat-v2-more',
};

/** The screen a frame is, where the list holds one. */
export const intakeSceneForFrame = (
  frame: Frame,
  journey?: IntakeJourney,
): string | undefined =>
  intakeScenes.find(
    (s) =>
      s.initial === frame &&
      (!journey || journeyOfSection(s.section) === journey),
  )?.id;

/**
 * Onboarding 1.1's screens: the same frames as the intake's happy path, drawn
 * with the warmer words and the give-backs (Flow's `version`). Its own journey
 * in the prototype, so it can be walked beside 1.0 rather than replacing it.
 */
const V11: [Frame, string][] = [
  ['youre-in', "You're in, saying what they get"],
  ['ai-consent', 'Before we start, plain and honest'],
  ['chat-v2-fork', 'Keni, named once'],
  ['chat-v2-carer-name', 'Worth knowing, after the fork'],
  ['chat-v2-preferred-name', 'What should we call you?'],
  ['chat-v2-mobile', 'Your mobile number'],
  ['chat-v2-who', 'What this is for, and who reads it'],
  ['chat-v2-situation', 'The open ask, warmer'],
  ['chat-v2-anything-more', 'Anything else?'],
  ['processing', 'The wait shows the work'],
  ['summary', 'Does this look right?'],
  ['summary-problem', 'What we did get'],
  ['review-facts', 'About Margaret'],
];

/**
 * Onboarding 2.0's screens: the dev concept, in the order the 2.0 flows page
 * draws its Dev concept row, opening in the chat with Keni and the consent
 * through to what to start with. Keep the two in step when either moves.
 */
const V20: [Frame, string][] = [
  // The Happy path, the same twelve in the same order and words as the flows
  // page's row. Everything after it is off that path.
  ['chat-v2', 'What we do, and the consent'],
  // Off while You're talking to Keni is hidden: it is that row tapped.
  // ['keni-about', 'Keni the llama'],
  ['consent-explain', 'How we use AI'],
  ['help-ask', 'The open question'],
  ['help-recording', 'Recording'],
  ['help-recording-captions', 'Recording, captions on'],
  ['help-processing', 'Reading what they said'],
  ['help-summary', 'Does this look right?'],
  ['help-questions', 'What the summary left'],
  ['help-age', 'How old they are'],
  ['help-phone', 'Your phone number'],
  ['help-start-talk', 'Where to start'],
  ['book-call-placeholder', 'Book a call page'],
  ['ai-policy-placeholder', 'AI policy'],
  ['help-start', 'Where to start, before picking'],
  ['home-placeholder', 'Home page, after Do this later'],
  ['help-phone-later', 'Phone number left for later'],
  ['help-under-18', 'Under 18'],
  [
    'help-need-help-first-summary',
    'Does this look right? Without what they need',
  ],
  ['help-need-help', 'What they most want help with'],
  ['help-need-help-reading', 'Reading what they want help with'],
  ['help-need-help-summary', 'The summary, with it in'],
  ['help-not-quite', 'Not quite'],
  ['help-summary-fixed', 'The summary, changed'],
  ['help-own-words-typed', 'In their own words'],
  ['help-typed', 'Typed instead'],
  ['help-photo', 'A photo instead'],
  ['help-too-short', 'Too short to read'],
  ['help-not-understood', "Couldn't make it out"],
  ['help-mic-blocked', 'Microphone blocked'],
  ['help-fail-send-voice', "Voice note didn't send"],
  ['help-fail-send', "Message didn't send"],
  ['help-phone-short', 'Phone number too short'],
  ['help-phone-long', 'Phone number too long'],
  ['help-fail-upload', "Photo didn't upload"],
  ['help-resumed', 'Coming back'],
  ['old-onboarding', "I'd rather not"],
  // Parked: the middle of the journey, off the main path for now and kept
  // for later. Each walks the whole journey (Flow's `full` branch).
  ['help-how', 'Parked: How we can help'],
  ['help-need', "Parked: What we'd need"],
  ['help-gather', 'Parked: Send us what you have'],
  ['help-gathered', 'Parked: Some of it sent'],
  ['help-collected', "Parked: Here's what we've got"],
  ['help-collected-editing', 'Parked: Correcting it'],
  ['help-how-scotland', 'Parked: In Scotland'],
  ['help-how-younger', 'Parked: Under pension age'],
  ['help-how-no-daily-help', 'Parked: No help needed day to day'],
  ['help-own-words-answered', 'Parked: Age already answered'],
  ['help-change-answer', 'Parked: Changing an answer'],
  ['help-change-answer-done', 'Parked: Answer changed'],
  ['help-not-now', 'Parked: Not right now'],
  ['help-later', "Parked: I'll do it later"],
];

intakeScenes.push(
  ...V20.map(([frame, label]): Scene<Frame> => ({
    id: `v20-${frame}`,
    label,
    kind: 'happy',
    initial: frame,
    section: 'v20',
  })),
);

/**
 * Option B, the A/B test's other side: what we do and the AI consent on screens
 * of their own before the chat. Kept apart from 2.0's screens and listed last,
 * so the prototype only walks it when it is picked. Its own You're in, because
 * the shared one now goes straight into the chat (option A).
 */
const V20_B: [Frame, string][] = [
  ['youre-in-b', '1B · What we do'],
  ['ai-consent', '2B · How we use AI'],
  ['help-ask-fresh', '3B · The chat, starting fresh'],
];

intakeScenes.push(
  ...V20_B.map(([frame, label]): Scene<Frame> => ({
    id: `v20-${frame}`,
    label,
    kind: 'happy',
    initial: frame,
    section: 'v20-b',
  })),
);

/**
 * Onboarding 4.1: the same screens as 4.0, drawn in 4.1's words (Flow's
 * version '2.1'). Its own new turns, like the consent to share, are reached
 * by answering through the chat rather than listed as screens.
 */
// 4.0's screens that 4.1 has no place for: the AI consent the opening no longer
// asks, and 4.0's missing-need path, which 4.1 asks at the end instead.
const NOT_IN_V21 = new Set<Frame>([
  'consent-explain',
  'ai-policy-placeholder',
  'help-need-help-first-summary',
  'help-need-help',
  'help-need-help-reading',
  'help-need-help-summary',
]);
const V21: [Frame, string][] = V20.filter(
  ([frame]) => !NOT_IN_V21.has(frame),
).map(([frame, label]) =>
  frame === 'chat-v2' ? [frame, 'What we do, about 3 minutes'] : [frame, label],
);

intakeScenes.push(
  ...V21.map(([frame, label]): Scene<Frame> => ({
    id: `v21-${frame}`,
    label,
    kind: 'happy',
    initial: frame,
    section: 'v21',
  })),
  ...V20_B.map(([frame, label]): Scene<Frame> => ({
    id: `v21-${frame}`,
    label,
    kind: 'happy',
    initial: frame,
    section: 'v21-b',
  })),
);

intakeScenes.push(
  ...V11.map(([frame, label]): Scene<Frame> => ({
    id: `v11-${frame}`,
    label,
    kind: frame === 'summary-problem' ? 'error' : 'happy',
    initial: frame,
    section: 'v11',
  })),
);
